import { deepAnalysisApi } from '../service';
import {
    ClusterAndAddServiceableUrlsParams,
    DeleteServiceableUrlParams,
    FetchServiceableUrlsCountParams,
    FetchServiceableUrlsCountResponse,
    FetchServiceableUrlsParams,
    FetchServiceableUrlsResponse,
    ServiceableUrl,
    FetchServiceableUrlsForClusterParams,
    AddServiceableUrlsInClusterParams,
} from './types';

const serviceableUrlsApi = deepAnalysisApi.injectEndpoints({
    endpoints: (builder) => ({
        fetchServiceableUrls: builder.query<FetchServiceableUrlsResponse, FetchServiceableUrlsParams>({
            query: (args) => {
                const { website_guid, limit, last_evaluated_key } = args;

                return {
                    method: 'GET',
                    url: `/serviceable-url?w_guid=${website_guid}${limit ? `&limit=${limit}` : ''}${
                        last_evaluated_key ? `&last_evaluated_key=${last_evaluated_key}` : ''
                    }`,
                };
            },

            providesTags: (result, error, args) => [{ type: 'ServiceableUrls', id: args.website_guid }],
        }),

        fetchServiceableUrlsCount: builder.query<FetchServiceableUrlsCountResponse, FetchServiceableUrlsCountParams>({
            query: (args) => {
                const { w_guid } = args;

                return {
                    method: 'GET',
                    url: `/serviceable-url/${w_guid}/count`,
                };
            },

            providesTags: (result, error, args) => [{ type: 'ServiceableUrlsCount', id: args.w_guid }],
        }),

        fetchServiceableUrlsForCluster: builder.query<ServiceableUrl[], FetchServiceableUrlsForClusterParams>({
            query: (args) => {
                const { w_guid, cluster_guid } = args;

                return {
                    method: 'GET',
                    url: `/serviceable-url/${w_guid}/cluster/${cluster_guid}`,
                };
            },

            providesTags: (result, error, args) => [{ type: 'ServiceableUrlsInCluster', id: args.w_guid }],
        }),

        clusterAndAddServiceableUrls: builder.mutation<ServiceableUrl[], ClusterAndAddServiceableUrlsParams>({
            query: (args) => {
                const { w_guid, urls } = args;

                return {
                    method: 'POST',
                    body: urls,
                    url: `/serviceable-url/${w_guid}`,
                };
            },

            invalidatesTags: (result, error, args) => [
                { type: 'ServiceableUrls', id: args.w_guid },
                { type: 'UrlClusters', id: args.w_guid },
                { type: 'ServiceableUrlsInCluster', id: args.w_guid },
                { type: 'ServiceableUrlsCount', id: args.w_guid },
            ],
        }),

        addServiceableUrlsInCluster: builder.mutation<ServiceableUrl[], AddServiceableUrlsInClusterParams>({
            query: (args) => {
                const { w_guid, cluster_guid, urls } = args;

                return {
                    method: 'POST',
                    body: urls,
                    url: `/serviceable-url/${w_guid}/cluster/${cluster_guid}`,
                };
            },

            invalidatesTags: (result, error, args) => [
                { type: 'ServiceableUrls', id: args.w_guid },
                { type: 'UrlClusters', id: args.w_guid },
                { type: 'ServiceableUrlsInCluster', id: args.w_guid },
                { type: 'ServiceableUrlsCount', id: args.w_guid },
            ],
        }),

        deleteServiceableUrl: builder.mutation<void, DeleteServiceableUrlParams>({
            query: (args) => {
                const { w_guid, url_guid } = args;

                return {
                    method: 'DELETE',
                    url: `/serviceable-url/${w_guid}/${url_guid}`,
                };
            },

            invalidatesTags: (result, error, args) => [
                { type: 'ServiceableUrls', id: args.w_guid },
                { type: 'UrlClusters', id: args.w_guid },
                { type: 'ServiceableUrlsInCluster', id: args.w_guid },
                { type: 'ServiceableUrlsCount', id: args.w_guid },
                { type: 'FailedChecks', id: args.w_guid },
                { type: 'AffectedUrls', id: args.w_guid },
            ],
        }),
    }),
});

export const {
    useClusterAndAddServiceableUrlsMutation,
    useAddServiceableUrlsInClusterMutation,
    useDeleteServiceableUrlMutation,
    useFetchServiceableUrlsCountQuery,
    useFetchServiceableUrlsQuery,
    useLazyFetchServiceableUrlsQuery,
    useFetchServiceableUrlsForClusterQuery,
} = serviceableUrlsApi;
